import { forwardRef } from 'react';

import { cx } from '../../lib/cx';
import { useControlId } from '../../lib/use-id';
import type { CheckableProps } from './types';

const base =
  'h-4 w-4 shrink-0 border-gray-300 text-brand-600 focus:ring-2 focus:ring-brand-100 focus:ring-offset-0 disabled:opacity-50';

const Checkable = forwardRef<HTMLInputElement, CheckableProps & { type: 'checkbox' | 'radio' }>(
  function Checkable({ label, description, id: providedId, className, type, ...props }, ref) {
    const id = useControlId(providedId);
    return (
      <label
        htmlFor={id}
        className={cx(
          'control-touch flex cursor-pointer items-start gap-2 py-1 text-sm',
          className,
        )}
      >
        <input
          ref={ref}
          id={id}
          type={type}
          className={cx(base, type === 'checkbox' ? 'rounded' : 'rounded-full', 'mt-0.5')}
          {...props}
        />
        <span className="flex flex-col">
          <span className="font-medium text-gray-800">{label}</span>
          {description ? <span className="text-xs text-gray-500">{description}</span> : null}
        </span>
      </label>
    );
  },
);

/**
 * A checkbox with its label as the click target.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckableProps>(function Checkbox(props, ref) {
  return <Checkable ref={ref} type="checkbox" {...props} />;
});

/**
 * A radio button with its label as the click target; group them with a shared `name`.
 */
export const Radio = forwardRef<HTMLInputElement, CheckableProps>(function Radio(props, ref) {
  return <Checkable ref={ref} type="radio" {...props} />;
});
